// SPDX-License-Identifier: AGPL-3.0-or-later

import {UnreadCatchUpController} from '@app/features/read_state/utils/UnreadCatchUpController';
import {runInAction} from 'mobx';
import {type RefObject, useCallback, useEffect, useRef} from 'react';

export function useUnreadCatchUp(options: {
	channelId: string;
	scrollerInnerRef: RefObject<HTMLElement | null>;
	getViewport: () => HTMLElement | null;
	isJumpActive: () => boolean;
	setHasOffScreenRemainingUnread: (value: boolean) => void;
	messagesReady: boolean;
	messageVersion: number;
	ackMessageId: string | null;
	visualUnreadMessageId: string | null;
	oldestUnreadMessageId: string | null;
	ackedManually: boolean;
	clearOnIdleAtBottom: boolean;
	tailGapPending?: boolean;
}): {
	onScroll: () => void;
	onUserScrollGesture: () => void;
	onScrollerKeyDown: (key: string) => void;
	onTextareaFocus: () => void;
} {
	const hostRef = useRef({
		getScroller: options.getViewport,
		getInner: () => options.scrollerInnerRef.current,
		isJumpActive: options.isJumpActive,
		isTailGapPending: () => options.tailGapPending === true,
		onRemainingUnread: options.setHasOffScreenRemainingUnread,
	});
	hostRef.current.getScroller = options.getViewport;
	hostRef.current.getInner = () => options.scrollerInnerRef.current;
	hostRef.current.isJumpActive = options.isJumpActive;
	hostRef.current.isTailGapPending = () => options.tailGapPending === true;
	hostRef.current.onRemainingUnread = options.setHasOffScreenRemainingUnread;
	const controllerRef = useRef<UnreadCatchUpController | null>(null);
	if (controllerRef.current == null) {
		controllerRef.current = new UnreadCatchUpController({
			getScroller: () => hostRef.current.getScroller(),
			getInner: () => hostRef.current.getInner(),
			isJumpActive: () => hostRef.current.isJumpActive(),
			isTailGapPending: () => hostRef.current.isTailGapPending(),
			onRemainingUnread: (value) => {
				runInAction(() => {
					hostRef.current.onRemainingUnread(value);
				});
			},
		});
	}
	const controller = controllerRef.current;
	useEffect(() => {
		controller.attach(options.channelId);
		return () => {
			controller.detach();
		};
	}, [controller, options.channelId]);
	useEffect(() => {
		controller.captureVisitAnchor(options.visualUnreadMessageId, options.oldestUnreadMessageId, options.ackMessageId);
	}, [controller, options.ackMessageId, options.oldestUnreadMessageId, options.visualUnreadMessageId]);
	useEffect(() => {
		controller.onLayoutChanged();
		const settleFrame = requestAnimationFrame(() => {
			controller.onLayoutChanged();
		});
		return () => {
			cancelAnimationFrame(settleFrame);
		};
	}, [
		controller,
		options.ackedManually,
		options.ackMessageId,
		options.channelId,
		options.messageVersion,
		options.messagesReady,
	]);
	useEffect(() => {
		controller.onIdleToggle(options.clearOnIdleAtBottom);
	}, [controller, options.clearOnIdleAtBottom]);
	const onScroll = useCallback(() => {
		controller.onScroll();
	}, [controller]);
	const onUserScrollGesture = useCallback(() => {
		controller.onUserScrollGesture();
	}, [controller]);
	const onScrollerKeyDown = useCallback(
		(key: string) => {
			controller.onScrollerKeyDown(key);
		},
		[controller],
	);
	const onTextareaFocus = useCallback(() => {
		controller.onTextareaFocus();
	}, [controller]);
	return {onScroll, onUserScrollGesture, onScrollerKeyDown, onTextareaFocus};
}
